import { Injectable } from '@angular/core';

const STORAGE_KEY = 'ggm-ov-login:last-portal';

export interface PortalPreference {
  portalCode: string;
  portalLabel: string;
  targetAppId?: string;
  savedAt: number;
}

@Injectable({ providedIn: 'root' })
export class PortalPreferenceService {
  public getLastPortal(): PortalPreference | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as PortalPreference;
      if (!parsed?.portalCode || !parsed?.portalLabel) {
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  }

  public saveLastPortal(preference: Omit<PortalPreference, 'savedAt'>): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    const value: PortalPreference = {
      ...preference,
      savedAt: Date.now(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  }

  public isLastPortal(destination: { portalCode: string; targetAppId: string }): boolean {
    const last = this.getLastPortal();
    if (!last) {
      return false;
    }

    if (last.targetAppId) {
      return last.targetAppId === destination.targetAppId;
    }

    return last.portalCode.toUpperCase() === destination.portalCode.toUpperCase();
  }

  public sortDestinations<T extends { portalCode: string; targetAppId: string }>(destinations: T[]): T[] {
    const last = this.getLastPortal();
    if (!last) {
      return destinations;
    }

    const remembered = destinations.filter((destination) => this.isLastPortal(destination));
    const others = destinations.filter((destination) => !this.isLastPortal(destination));
    return [...remembered, ...others];
  }
}
